import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch } from '@store/hooks/core';

import type { ArtifactUploadResponse } from '../types';

import {
  type OrganizationArtifactIntakeHubContext,
  patientArtifactsApi,
  useAdminArtifactUploadConstraintsQuery,
  useArtifactUploadConstraintsQuery,
  useDeleteDocumentReferenceMutation,
  useGetDocumentReferenceQuery,
  useGetPatientDocumentReferencesQuery,
  useGetPatientTimelineQuery,
  useLazyGetArtifactDownloadUrlQuery,
  useUploadAdminOrganizationArtifactsMutation,
  useUploadOrganizationArtifactsMutation,
  useUploadPatientArtifactsMutation,
} from './api';
import {
  type ArtifactEvent,
  type ArtifactEventDocumentReference,
  type DocRefExtractionEvent,
  subscribeToAdminOrganizationArtifactEvents,
  subscribeToOrganizationArtifactEvents,
  subscribeToPatientArtifactEvents,
} from './artifactSse';
import {
  DEFAULT_UPLOAD_CONSTRAINTS,
  formatAllowedTypes,
  formatBytes,
  parseConflictResponse,
  type UploadOutcome,
} from './uploadValidation';

export type TimelineLens = 'all' | 'clinical' | 'operational' | 'admin';
export type TimelineKind =
  | 'document'
  | 'encounter'
  | 'payment_event'
  | 'communication'
  | 'form'
  | 'medication_event';

export interface UsePatientTimelineOptions {
  patientId: string;
  lens?: TimelineLens;
  kinds?: TimelineKind[] | null;
  /** Server-side doc_kind filter — applied only to document envelopes. */
  docKinds?: string[] | null;
  /** Server-side full-text search over document title + description. */
  q?: string | null;
  since?: string | null;
  before?: string | null;
  limit?: number;
  cursor?: string | null;
  skip?: boolean;
}

/**
 * Unified timeline read. Returns roots in `items` (newest-first) plus a
 * `threads` map keyed by encounter_id (children oldest-first within each
 * thread). SSE-driven invalidation is wired in `store/api.ts` —
 * artifact-level lifecycle events trigger a refetch of this query, which
 * picks up newly-decomposed DocRefs and enrichment results.
 */
export function usePatientTimeline(options: UsePatientTimelineOptions) {
  const {
    patientId,
    lens = 'all',
    kinds,
    docKinds,
    q,
    since,
    before,
    limit = 50,
    cursor,
    skip = false,
  } = options;

  // RTK Query's default URLSearchParams serializer turns `null` into the
  // literal string `"null"`, which the backend's Pydantic validators reject.
  // Coerce nullables to `undefined` so the params are omitted entirely
  // when not set.
  const query = useGetPatientTimelineQuery(
    {
      patientId,
      lens,
      kinds: kinds ?? undefined,
      docKinds: docKinds ?? undefined,
      q: q ?? undefined,
      since: since ?? undefined,
      before: before ?? undefined,
      limit,
      cursor: cursor ?? undefined,
    },
    { skip: skip || !patientId },
  );

  return {
    items: query.data?.items ?? [],
    threads: query.data?.threads ?? {},
    nextCursor: query.data?.next_cursor ?? null,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

/** Detail fetch for the receipt drawer. */
export function useDocumentReference(documentReferenceId: string | null) {
  const query = useGetDocumentReferenceQuery(
    { documentReferenceId: documentReferenceId ?? '' },
    { skip: !documentReferenceId },
  );
  return {
    documentReference: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

export interface UsePatientDocumentReferencesOptions {
  patientId: string;
  sourceArtifactId?: string | null;
  encounterId?: string | null;
  limit?: number;
  offset?: number;
  skip?: boolean;
}

/**
 * Direct DocRef list — useful for "show all records derived from this
 * upload" (drawer side panel) or "all records on this encounter".
 */
export function usePatientDocumentReferences(options: UsePatientDocumentReferencesOptions) {
  const {
    patientId,
    sourceArtifactId,
    encounterId,
    limit = 50,
    offset = 0,
    skip = false,
  } = options;

  const query = useGetPatientDocumentReferencesQuery(
    {
      patientId,
      sourceArtifactId: sourceArtifactId ?? undefined,
      encounterId: encounterId ?? undefined,
      limit,
      offset,
    },
    { skip: skip || !patientId },
  );

  return {
    items: query.data?.items ?? [],
    total: query.data?.total ?? 0,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

export interface UseUploadPatientArtifactsResult {
  /** Returns a normalized outcome with both successful artifacts (which
   *  the caller should pipe into `useOptimisticUploads.trackUpload`)
   *  and any duplicates (which should be surfaced as snackbars).
   *  Resolves to `null` only when patientId is missing or the file list
   *  is empty. Throws on non-conflict errors so the caller can format
   *  them via `formatUploadLimitError`. */
  upload: (files: File[]) => Promise<UploadOutcome | null>;
  isUploading: boolean;
  error: unknown;
}

/** Multipart upload of one or more files for a patient. */
export function useUploadPatientArtifacts(patientId: string): UseUploadPatientArtifactsResult {
  const [uploadArtifacts, { isLoading, error }] = useUploadPatientArtifactsMutation();

  const upload = useCallback(
    async (files: File[]): Promise<UploadOutcome | null> => {
      if (!patientId || files.length === 0) return null;
      try {
        const response = await uploadArtifacts({ patientId, files }).unwrap();
        return {
          artifacts: response.artifacts ?? [],
          duplicates: [],
          artifact_batch_id: response.artifact_batch_id ?? null,
        };
      } catch (err) {
        // 409 conflict: some/all files collided with existing artifacts.
        // Treat as a partial success so the caller can ghost the new
        // ones and toast the duplicates instead of swallowing both as
        // an opaque error.
        const conflict = parseConflictResponse(err);
        if (conflict) return conflict;
        throw err;
      }
    },
    [patientId, uploadArtifacts],
  );

  return { upload, isUploading: isLoading, error };
}

export interface UseUploadOrganizationArtifactsResult {
  upload: (
    files: File[],
    options?: {
      sourceChannel?: string;
      intakeHub?: OrganizationArtifactIntakeHubContext;
    },
  ) => Promise<UploadOutcome | null>;
  isUploading: boolean;
  error: unknown;
}

/**
 * Multipart upload of one or more files at the org scope (no patient).
 * GDN-1032 — feeds the same artifact pipeline as the patient route, but
 * the resulting DocRefs land with `patient_id=null` (e.g. clinic policy
 * PDFs).
 */
export function useUploadOrganizationArtifacts(
  organizationId: string | null | undefined,
): UseUploadOrganizationArtifactsResult {
  const [uploadArtifacts, { isLoading, error }] = useUploadOrganizationArtifactsMutation();

  const upload = useCallback(
    async (
      files: File[],
      options?: {
        sourceChannel?: string;
        intakeHub?: OrganizationArtifactIntakeHubContext;
      },
    ): Promise<UploadOutcome | null> => {
      if (!organizationId || files.length === 0) return null;
      try {
        const response = await uploadArtifacts({
          organizationId,
          files,
          sourceChannel: options?.sourceChannel,
          intakeHub: options?.intakeHub,
        }).unwrap();
        return {
          artifacts: response.artifacts ?? [],
          duplicates: [],
          artifact_batch_id: response.artifact_batch_id ?? null,
        };
      } catch (err) {
        const conflict = parseConflictResponse(err);
        if (conflict) return conflict;
        throw err;
      }
    },
    [organizationId, uploadArtifacts],
  );

  return { upload, isUploading: isLoading, error };
}

/** Admin-scoped sibling for support uploading into a target organization. */
export function useUploadAdminOrganizationArtifacts(
  organizationId: string | null | undefined,
): UseUploadOrganizationArtifactsResult {
  const [uploadArtifacts, { isLoading, error }] = useUploadAdminOrganizationArtifactsMutation();

  const upload = useCallback(
    async (
      files: File[],
      options?: {
        sourceChannel?: string;
        intakeHub?: OrganizationArtifactIntakeHubContext;
      },
    ): Promise<UploadOutcome | null> => {
      if (!organizationId || files.length === 0) return null;
      try {
        const response = await uploadArtifacts({
          organizationId,
          files,
          sourceChannel: options?.sourceChannel,
          intakeHub: options?.intakeHub,
        }).unwrap();
        return {
          artifacts: response.artifacts ?? [],
          duplicates: [],
          artifact_batch_id: response.artifact_batch_id ?? null,
        };
      } catch (err) {
        const conflict = parseConflictResponse(err);
        if (conflict) return conflict;
        throw err;
      }
    },
    [organizationId, uploadArtifacts],
  );

  return { upload, isUploading: isLoading, error };
}

export function useDeleteDocumentReference() {
  const [deleteDocRef, { isLoading, error }] = useDeleteDocumentReferenceMutation();
  const remove = useCallback(
    async (documentReferenceId: string) => {
      await deleteDocRef({ documentReferenceId }).unwrap();
    },
    [deleteDocRef],
  );
  return { remove, isDeleting: isLoading, error };
}

/**
 * One in-flight upload as far as the FE is concerned: an artifact has
 * been created by the BE (we have its id), but the Fast Card pipeline
 * hasn't produced a DocRef yet. Surfaces render this as a ghost row /
 * skeleton card and reconcile against `source_artifact_id` once the
 * real DocRef arrives via SSE-driven refetch.
 *
 * Once an enriched artifact lifecycle event fires, the SSE payload carries the
 * minimal DocRef heads (id + title + doc_kind), which we attach here
 * so the ghost can swap from "filename + spinner" to "real title +
 * kind badge + spinner" before the list refetch lands.
 */
export interface PendingDocumentUpload {
  /** Matches `source_artifact_id` on the eventual DocRef row. */
  artifactId: string;
  filename: string;
  /** processing_status from the artifact ack (`uploaded` / `parsing` / …). */
  status: string;
  /** Heads from an enriched artifact lifecycle event. Empty array means
   *  Fast Card produced no clinical records (e.g. an admin contract). */
  documentReferences?: ArtifactEventDocumentReference[];
  extractionSignals?: PendingExtractionSignal[];
  /** ms-since-epoch when the upload was tracked. Used by the safety-net
   *  expiry sweep to dismiss ghosts that never received a terminal
   *  SSE event (network flap, server restart, BE worker crash). */
  uploadedAt: number;
}

export interface PendingExtractionSignal {
  id: string;
  label: string;
}

/**
 * How long an unresolved ghost stays around before the safety-net
 * sweep dismisses it. Generous because the happy-path pipeline
 * usually finishes inside ~30 seconds — anything past 5 min means
 * the SSE never delivered a terminal event and the user is stuck
 * staring at a "processing…" row that won't move. Matches the
 * timeline view's PENDING_UPLOAD_TIMEOUT_MS for consistency.
 */
const PENDING_UPLOAD_TIMEOUT_MS = 5 * 60 * 1000;
const PENDING_UPLOAD_SWEEP_INTERVAL_MS = 30 * 1000;

interface UseOptimisticUploadsResult {
  pendingUploads: PendingDocumentUpload[];
  /** Append rows from an artifact upload response. No-op on null/empty. */
  trackUpload: (response: ArtifactUploadResponse | null | undefined) => void;
  /** Drop a single ghost when its DocRef has shown up. */
  dismissByArtifactId: (artifactId: string) => void;
  /**
   * Drop every ghost whose `artifactId` is now present as
   * `source_artifact_id` on one of the items. Use this when the
   * component owning the hook also owns the loaded list (e.g.
   * `/documents`); for parent-owns-state-but-child-has-data setups
   * (e.g. v1 patient page → PatientDocumentsBlock) wire
   * `dismissByArtifactId` per match instead.
   */
  reconcile: (items: { source_artifact_id?: string | null }[]) => void;
  /**
   * Wire to `usePatientArtifactEventStream({ onEvent })`. Advances the
   * ghost's caption through `parsing` / `parsed` / `decomposed` and
   * dismisses on terminal events (`processed` / `failed`) — important
   * for artifacts that reach a terminal state without producing a
   * DocRef (e.g. an admin contract that Fast Card decides has nothing
   * clinical to extract), where reconcile-by-source_artifact_id would
   * never fire.
   */
  handleArtifactEvent: (event: ArtifactEvent) => void;
  handleDocRefExtraction: (event: DocRefExtractionEvent) => void;
}

/**
 * Optimistic-UI plumbing for artifact uploads. Owned by whichever
 * surface renders the ghosts; pair with `useUploadPatientArtifacts` /
 * `useUploadOrganizationArtifacts` and feed the upload response into
 * `trackUpload` to make a row appear immediately.
 */
export function useOptimisticUploads(): UseOptimisticUploadsResult {
  const [pendingUploads, setPendingUploads] = useState<PendingDocumentUpload[]>([]);

  const trackUpload = useCallback((response: ArtifactUploadResponse | null | undefined) => {
    if (!response?.artifacts?.length) return;
    const now = Date.now();
    setPendingUploads(prev => [
      ...response.artifacts.map(a => ({
        artifactId: a.artifact_id,
        filename: a.original_filename ?? 'Untitled',
        status: a.processing_status ?? 'uploaded',
        uploadedAt: now,
      })),
      ...prev,
    ]);
  }, []);

  // Safety-net sweep: drop ghosts that have been pending past the
  // timeout. Covers the case where the SSE connection drops silently
  // (the helper's `onerror() {}` swallows it) so `processed`/`failed`
  // never fire and the ghost would otherwise stay forever.
  useEffect(() => {
    if (pendingUploads.length === 0) return;
    const sweep = () => {
      const cutoff = Date.now() - PENDING_UPLOAD_TIMEOUT_MS;
      setPendingUploads(prev => {
        const next = prev.filter(p => p.uploadedAt > cutoff);
        return next.length === prev.length ? prev : next;
      });
    };
    const id = window.setInterval(sweep, PENDING_UPLOAD_SWEEP_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [pendingUploads.length]);

  const dismissByArtifactId = useCallback((artifactId: string) => {
    setPendingUploads(prev => prev.filter(p => p.artifactId !== artifactId));
  }, []);

  const reconcile = useCallback((items: { source_artifact_id?: string | null }[]) => {
    const seen = new Set<string>();
    for (const item of items) {
      if (item.source_artifact_id) seen.add(item.source_artifact_id);
    }
    setPendingUploads(prev => {
      const next = prev.filter(p => !seen.has(p.artifactId));
      return next.length === prev.length ? prev : next;
    });
  }, []);

  const handleArtifactEvent = useCallback((event: ArtifactEvent) => {
    setPendingUploads(prev => {
      const idx = prev.findIndex(p => p.artifactId === event.artifactId);
      if (idx === -1) return prev;
      // Terminal events: drop the ghost. `processed` means the BE is
      // done with the file (whether or not a DocRef was extracted);
      // `failed` means it gave up. Either way nothing more to wait for.
      if (event.name === 'artifact.processed' || event.name === 'artifact.failed') {
        return prev.filter((_, i) => i !== idx);
      }
      const current = prev[idx];
      // Enriched lifecycle events can carry the extracted DocRef heads.
      // Patch them onto the ghost so the card can swap "filename" for
      // the extracted title before the list refetch lands.
      const nextRefs = event.documentReferences ?? current.documentReferences;
      if (current.status === event.status && nextRefs === current.documentReferences) {
        return prev;
      }
      const next = prev.slice();
      next[idx] = {
        ...current,
        status: event.status,
        documentReferences: nextRefs,
      };
      return next;
    });
  }, []);

  const handleDocRefExtraction = useCallback((event: DocRefExtractionEvent) => {
    setPendingUploads(prev => {
      const idx = prev.findIndex(p => p.artifactId === event.artifactId);
      if (idx === -1) return prev;
      const signals = extractionSignalsFromEvent(event);
      if (signals.length === 0) return prev;

      const current = prev[idx];
      const seen = new Set((current.extractionSignals ?? []).map(signal => signal.id));
      const nextSignals = [
        ...(current.extractionSignals ?? []),
        ...signals.filter(signal => !seen.has(signal.id)),
      ];
      if (nextSignals.length === (current.extractionSignals ?? []).length) return prev;

      const next = prev.slice();
      next[idx] = {
        ...current,
        extractionSignals: nextSignals,
      };
      return next;
    });
  }, []);

  return {
    pendingUploads,
    trackUpload,
    dismissByArtifactId,
    reconcile,
    handleArtifactEvent,
    handleDocRefExtraction,
  };
}

function extractionSignalsFromEvent(event: DocRefExtractionEvent): PendingExtractionSignal[] {
  const signals: PendingExtractionSignal[] = [];
  const addSignal = (kind: string, value: unknown) => {
    if (typeof value !== 'string' || value.trim().length === 0) return;
    const key = value.trim();
    signals.push({
      id: `${event.documentReferenceId}:${kind}:${key}`,
      label: humanizeExtractionKey(key),
    });
  };

  const matchedConcept = event.summary.matched_concept_key;
  addSignal('concept', matchedConcept);

  const matches = Array.isArray(event.summary.matches) ? event.summary.matches : [];
  for (const match of matches) {
    if (match && typeof match === 'object') {
      addSignal('concept', (match as Record<string, unknown>).concept_key);
    }
  }

  const extractedFields = event.summary.extracted_fields;
  if (extractedFields && typeof extractedFields === 'object' && !Array.isArray(extractedFields)) {
    for (const key of Object.keys(extractedFields)) {
      addSignal('field', key);
    }
  }

  return signals;
}

function humanizeExtractionKey(key: string): string {
  const acronyms = new Set(['api', 'caqh', 'dea', 'ein', 'id', 'npi', 'ssn', 'tin', 'w9']);
  return key
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map(part => {
      const lower = part.toLowerCase();
      return acronyms.has(lower) ? lower.toUpperCase() : lower[0].toUpperCase() + lower.slice(1);
    })
    .join(' ');
}

interface UseArtifactEventStreamOptions {
  /** When false, no SSE connection is opened. Flipping false closes
   *  any open connection. Default true. */
  enabled?: boolean;
  /** Per-event hook — typically `useOptimisticUploads().handleArtifactEvent`
   *  to drive ghost captions and terminal-state dismissal. */
  onEvent?: (event: ArtifactEvent) => void;
  /** Fires when the BE emits `document_reference.extraction_ready` for
   *  a DocRef on this stream. The hook also dispatches a default
   *  invalidation for that DocRef's detail cache so any open detail
   *  view picks up the new extraction metadata automatically. */
  onDocRefExtraction?: (event: DocRefExtractionEvent) => void;
}

/**
 * Open a patient-scoped artifact SSE stream and invalidate every derived
 * cache (patient docs list, org docs list, timeline, DocRef list) on
 * lifecycle events. Mount this only while you actually need updates —
 * typically while there are pending uploads — so the connection closes
 * as soon as enrichment finishes.
 *
 * Pre-GDN-1032 the SSE was opened inside RTK Query's `onCacheEntryAdded`
 * on the patient docs list query, which keyed connections by every
 * filter/page arg. Active filtering would stack overlapping connections
 * for 60s each (`keepUnusedDataFor`). Owning the lifecycle in a hook
 * gives the caller direct control: one connection per patient while
 * `enabled` is true, none otherwise.
 */
export function usePatientArtifactEventStream(
  patientId: string | null | undefined,
  { enabled = true, onEvent, onDocRefExtraction }: UseArtifactEventStreamOptions = {},
): void {
  const dispatch = useAppDispatch();
  // Latch the latest callbacks in refs so we don't re-subscribe (and
  // re-open the SSE) every time the parent passes a new closure.
  const onEventRef = useRef(onEvent);
  const onDocRefExtractionRef = useRef(onDocRefExtraction);
  useEffect(() => {
    onEventRef.current = onEvent;
    onDocRefExtractionRef.current = onDocRefExtraction;
  }, [onEvent, onDocRefExtraction]);

  useEffect(() => {
    if (!enabled || !patientId) return;
    const controller = new AbortController();
    const stream = subscribeToPatientArtifactEvents(patientId, {
      controller,
      onInvalidate: () => {
        dispatch(
          patientArtifactsApi.util.invalidateTags([
            { type: 'PatientDocumentsList', id: patientId },
            { type: 'PatientDocumentsList', id: 'LIST' },
            { type: 'DocumentList', id: 'LIST' },
            { type: 'PatientTimeline', id: patientId },
            { type: 'DocumentReferenceList', id: patientId },
          ]),
        );
      },
      onEvent: event => onEventRef.current?.(event),
      onDocRefExtraction: event => {
        // Default behavior: invalidate the DocRef detail + the unified
        // document detail keyed off this id so any open modal picks up
        // the new metadata.extractions blob without a manual refresh.
        dispatch(
          patientArtifactsApi.util.invalidateTags([
            { type: 'DocumentReference', id: event.documentReferenceId },
            { type: 'Document', id: event.documentReferenceId },
          ]),
        );
        onDocRefExtractionRef.current?.(event);
      },
    });
    return () => {
      controller.abort();
      // The stream promise is resolved by abort; awaited here only to
      // surface any cleanup errors during dev.
      void stream;
    };
  }, [enabled, patientId, dispatch]);
}

/**
 * Org-scoped sibling. Invalidates the unified docs list when an
 * org-scoped artifact lifecycle event fires.
 */
export function useOrganizationArtifactEventStream(
  organizationId: string | null | undefined,
  { enabled = true, onEvent, onDocRefExtraction }: UseArtifactEventStreamOptions = {},
): void {
  const dispatch = useAppDispatch();
  const onEventRef = useRef(onEvent);
  const onDocRefExtractionRef = useRef(onDocRefExtraction);
  useEffect(() => {
    onEventRef.current = onEvent;
    onDocRefExtractionRef.current = onDocRefExtraction;
  }, [onEvent, onDocRefExtraction]);

  useEffect(() => {
    if (!enabled || !organizationId) return;
    const controller = new AbortController();
    const stream = subscribeToOrganizationArtifactEvents(organizationId, {
      controller,
      onInvalidate: () => {
        dispatch(patientArtifactsApi.util.invalidateTags([{ type: 'DocumentList', id: 'LIST' }]));
      },
      onEvent: event => onEventRef.current?.(event),
      onDocRefExtraction: event => {
        dispatch(
          patientArtifactsApi.util.invalidateTags([
            { type: 'DocumentReference', id: event.documentReferenceId },
            { type: 'Document', id: event.documentReferenceId },
          ]),
        );
        onDocRefExtractionRef.current?.(event);
      },
    });
    return () => {
      controller.abort();
      void stream;
    };
  }, [enabled, organizationId, dispatch]);
}

/** Admin org-scoped sibling for support surfaces targeting another org. */
export function useAdminOrganizationArtifactEventStream(
  organizationId: string | null | undefined,
  { enabled = true, onEvent, onDocRefExtraction }: UseArtifactEventStreamOptions = {},
): void {
  const dispatch = useAppDispatch();
  const onEventRef = useRef(onEvent);
  const onDocRefExtractionRef = useRef(onDocRefExtraction);
  useEffect(() => {
    onEventRef.current = onEvent;
    onDocRefExtractionRef.current = onDocRefExtraction;
  }, [onEvent, onDocRefExtraction]);

  useEffect(() => {
    if (!enabled || !organizationId) return;
    const controller = new AbortController();
    const stream = subscribeToAdminOrganizationArtifactEvents(organizationId, {
      controller,
      onInvalidate: () => {
        dispatch(patientArtifactsApi.util.invalidateTags([{ type: 'DocumentList', id: 'LIST' }]));
      },
      onEvent: event => onEventRef.current?.(event),
      onDocRefExtraction: event => {
        dispatch(
          patientArtifactsApi.util.invalidateTags([
            { type: 'DocumentReference', id: event.documentReferenceId },
            { type: 'Document', id: event.documentReferenceId },
          ]),
        );
        onDocRefExtractionRef.current?.(event);
      },
    });
    return () => {
      controller.abort();
      void stream;
    };
  }, [enabled, organizationId, dispatch]);
}

/**
 * Resolve the BE upload limits, with sensible local defaults so the FE
 * can validate before the constraints query lands. Result is suitable
 * for direct use with `validateFiles`.
 */
export function useArtifactUploadConstraints(): {
  constraints: typeof DEFAULT_UPLOAD_CONSTRAINTS;
  /** Human-friendly summary, e.g. "PDF, JPG, PNG up to 25 MB". */
  summary: string;
} {
  const { data } = useArtifactUploadConstraintsQuery();
  return useMemo(() => {
    const constraints = data ?? DEFAULT_UPLOAD_CONSTRAINTS;
    const summary = `${formatAllowedTypes(constraints.allowed_content_types)} up to ${formatBytes(constraints.max_file_size_bytes)}`;
    return { constraints, summary };
  }, [data]);
}

export function useAdminArtifactUploadConstraints(): {
  constraints: typeof DEFAULT_UPLOAD_CONSTRAINTS;
  /** Human-friendly summary, e.g. "PDF, JPG, PNG up to 25 MB". */
  summary: string;
} {
  const { data } = useAdminArtifactUploadConstraintsQuery();
  return useMemo(() => {
    const constraints = data ?? DEFAULT_UPLOAD_CONSTRAINTS;
    const summary = `${formatAllowedTypes(constraints.allowed_content_types)} up to ${formatBytes(constraints.max_file_size_bytes)}`;
    return { constraints, summary };
  }, [data]);
}

/**
 * Imperative helper: lazy-fetch a presigned download URL by `artifact_id`.
 * The download is still keyed off the raw artifact (DocRefs don't have
 * their own files) — `source_artifact_id` from a DocRef is what you pass.
 */
export function useArtifactDownload() {
  const [trigger] = useLazyGetArtifactDownloadUrlQuery();
  return useCallback(
    async (artifactId: string): Promise<string> => {
      const result = await trigger({ artifactId }).unwrap();
      return result.url;
    },
    [trigger],
  );
}
